import i18n from '@/plugins/i18n';
import { DrawTooltip } from '@/plugins/podolib/services/charts/prosup-chart/angles-prosup-running-tooltips';
import { EPos, ERange } from '@/plugins/podolib/services/charts/gaitline/gaitline';
export enum ESide {
  left = 'left',
  right = 'right',
}
export type CONTAINER = {
  sideLegend: {
    left: POSITION;
    right: POSITION;
  };
  middle: number;
};
export type POSITION = {
  startX: number;
  endX: number;
};

export type TDatas = {
  [key: string]: any;
  propulsionPhase: TSidePhases;
  brakingPhase: TSidePhases;
  propulsion: TAngles;
  flatFoot: TAngles;
  strike: TAngles;
};
export type TSidePhases = {
  [key: string]: any;
  id: string;
  left: TPhases;
  right: TPhases;
};
export type TPhases = {
  abs: number;
  rel: number;
  ranges: {
    background: string;
    text: string;
    chevron: string;
  };
};
export type TAngles = {
  [key: string]: any;
  left: number;
  right: number;
  leftStd: number;
  rightStd: number;
};

export class DrawAnglesChartSVG {
  // Val
  private readonly canvas: HTMLCanvasElement;
  private readonly tooltipcanvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private tooltipGenerator: any;

  private readonly width: number;
  private readonly height: number;

  private readonly container: any = {
    sideLegend: {
      left: {
        startX: 0,
        endX: 0,
      },
      right: {
        startX: 0,
        endX: 0,
      },
    },
    chart: {
      height: 0,
      width: 0,
      paddingTop: 80,
      paddingBottom: 40,
      scale: {
        min: -30,
        max: 10,
        zones: [0, 10, 20, 30, 40, 50],
      },
    },
    middle: 0,
  };
  private readonly corridor: any = {
    propulsion: {
      min: 0,
      max: 0,
    },
    flatFoot: {
      min: 0,
      max: 0,
    },
    strike: {
      min: 0,
      max: 0,
    },
  };
  private tooltips: any = {
    pos: {
      strike: {
        left: {
          x: 0,
          y: 0,
        },
        right: {
          x: 0,
          y: 0,
        },
      },
      flatFoot: {
        left: {
          x: 0,
          y: 0,
        },
        right: {
          x: 0,
          y: 0,
        },
      },
      propulsion: {
        left: {
          x: 0,
          y: 0,
        },
        right: {
          x: 0,
          y: 0,
        },
      },
    },
  };
  private values: TDatas = {
    propulsionPhase: {
      id: '',
      left: { abs: 0, rel: 0, ranges: { background: '', text: '', chevron: '' } },
      right: { abs: 0, rel: 0, ranges: { background: '', text: '', chevron: '' } },
    },
    brakingPhase: {
      id: '',
      left: { abs: 0, rel: 0, ranges: { background: '', text: '', chevron: '' } },
      right: { abs: 0, rel: 0, ranges: { background: '', text: '', chevron: '' } },
    },
    propulsion: {
      left: 0,
      right: 0,
      leftStd: 0,
      rightStd: 0,
    },
    flatFoot: {
      left: 0,
      right: 0,
      leftStd: 0,
      rightStd: 0,
    },
    strike: {
      left: 0,
      right: 0,
      leftStd: 0,
      rightStd: 0,
    },
  };
  private readonly mode: string = 'abs';
  private unit = 'ms';

  private posY: any = {
    left: {
      propulsion: 0,
      flatFoot: 0,
      strike: 0,
    },
    right: {
      propulsion: 0,
      flatFoot: 0,
      strike: 0,
    },
  };

  constructor(
    canvas: HTMLCanvasElement,
    tooltipcanvas: HTMLCanvasElement,
    parentWidth: number,
    values: any,
    corridor: any,
    mode: string,
  ) {
    this.canvas = canvas;
    this.tooltipcanvas = tooltipcanvas;
    this.ctx = canvas.getContext('2d')!;

    this.width = parentWidth;
    this.height = this.canvas.height;

    this.mode = mode;
    this.corridor = corridor;
    this.ifRelativeMode(values);

    this.unit = mode === 'abs' ? 'ms' : '%';

    // Define chart positions
    this.container.sideLegend.left.endX = this.width * 0.2;
    this.container.sideLegend.right.startX = this.width * 0.8;
    this.container.sideLegend.right.endX = this.width;
    this.container.chart.width = this.width * 0.6;
    this.container.chart.height = this.height - this.container.chart.paddingTop - this.container.chart.paddingBottom;
    this.container.middle = this.width / 2;

    this.getTooltipZonePostion();
    this.getYPositionOfWalkPhases();
    this.getChartScale();
  }

  async draw() {
    this.clean();
    // Draw
    this.runPhases();
    this.angleRangesLines();
    this.contentStructure();
    // this.angleCorridor();
    this.angleLinesAndPoints();
    this.angleStdLines();

    this.tooltipGenerator = new DrawTooltip(
      this.tooltipcanvas,
      this.container,
      this.values,
      this.corridor,
      this.tooltips.pos,
      this.mode,
    );
    requestAnimationFrame(() => {
      this.tooltipGenerator.drawTooltip();
    });
  }

  // Structure
  contentStructure() {
    // GLOBALS
    const that: any = this;
    const startY = this.container.chart.paddingTop - 10;
    const endY = this.container.chart.height + this.container.chart.paddingTop + 10;
    const startYLetter = this.container.chart.paddingTop - 20;
    const imgScale: number = 40;
    const images: any = {
      left: {
        img: new Image(),
        posX: this.container.sideLegend.left.endX + this.container.chart.width / 4 - imgScale / 2,
      },
      right: {
        img: new Image(),
        posX: this.container.sideLegend.right.startX - this.container.chart.width / 4 + imgScale / 2,
      },
      width: 40,
      height: 35,
    };
    //
    this.ctx!.lineWidth = 2;
    this.ctx!.strokeStyle = '#aeb1be';
    this.ctx!.fillStyle = 'rgb(108,113,122)';
    this.ctx!.font = '600 12px Open Sans';

    // --- LEFT --- //

    // text
    this.ctx!.textAlign = 'left';
    this.ctx!.fillText(
      i18n.t('commons.standards.supinator') as string,
      this.container.sideLegend.left.endX,
      startYLetter,
    );
    // bar
    this.ctx!.beginPath();
    this.ctx!.moveTo(this.container.sideLegend.left.endX, startY);
    this.ctx!.lineTo(this.container.sideLegend.left.endX, endY);
    this.ctx!.stroke();

    // --- MIDDLE --- //

    // text
    this.ctx!.textAlign = 'center';
    this.ctx!.fillText(i18n.t('commons.standards.pronator') as string, this.container.middle, startYLetter);
    // bar
    this.ctx!.beginPath();
    this.ctx!.moveTo(this.container.middle, startY);
    this.ctx!.lineTo(this.container.middle, endY);
    this.ctx!.stroke();

    // --- RIGHT --- //

    // text
    this.ctx!.textAlign = 'right';
    this.ctx!.fillText(
      i18n.t('commons.standards.supinator') as string,
      this.container.sideLegend.right.startX,
      startYLetter,
    );
    // bar
    this.ctx!.beginPath();
    this.ctx!.moveTo(this.container.sideLegend.right.startX, startY);
    this.ctx!.lineTo(this.container.sideLegend.right.startX, endY);
    this.ctx!.stroke();

    // --- IMG --- //

    this.ctx!.font = '400 11px Open Sans';
    this.ctx!.fillStyle = '#8e919a';
    this.ctx!.textAlign = 'center';

    // LEFT
    images.left.img.onload = function () {
      that.ctx!.drawImage(images.left.img, images.left.posX, 10, images.width, images.height);
    };
    images.left.img.src = require(`@/assets/images/toe-strike-shoe.svg`);
    // legend
    this.ctx!.fillText(i18n.t(`commons.standards.left-feet`) as string, images.left.posX + imgScale / 2, startYLetter);

    // RIGHT
    images.right.img.onload = function () {
      that.ctx!.save();
      that.ctx!.scale(-1, 1);
      that.ctx!.drawImage(images.right.img, images.right.posX * -1, 10, images.width, images.height);
    };
    images.right.img.src = require(`@/assets/images/toe-strike-shoe.svg`);
    // legend
    this.ctx!.fillText(
      i18n.t(`commons.standards.right-feet`) as string,
      images.right.posX - imgScale / 2,
      startYLetter,
    );
  }
  // Vertical bar for angles
  angleRangesLines() {
    // GLOBALS
    const singleChartScale = (Math.abs(this.container.chart.scale.max) + Math.abs(this.container.chart.scale.min)) / 4;
    const singleChartWidth = this.container.chart.width / 2;
    const singleChartWidthSection = singleChartWidth / 4;
    const paddingY = this.container.chart.height + this.container.chart.paddingTop + 20;
    const paddingX = {
      left: this.container.sideLegend.left.endX,
      right: this.container.sideLegend.left.endX + singleChartWidth,
    };
    const ranges: any = {
      left: {
        low: {
          posX: paddingX.left + singleChartWidthSection,
          value: this.container.chart.scale.max - singleChartScale * 3,
        },
        neutral: {
          posX: paddingX.left + singleChartWidthSection * 2,
          value: this.container.chart.scale.max - singleChartScale * 2,
        },
        high: {
          posX: paddingX.left + singleChartWidthSection * 3,
          value: this.container.chart.scale.max - singleChartScale,
        },
      },
      right: {
        high: {
          posX: paddingX.right + singleChartWidthSection,
          value: this.container.chart.scale.max - singleChartScale,
        },
        neutral: {
          posX: paddingX.right + singleChartWidthSection * 2,
          value: this.container.chart.scale.max - singleChartScale * 2,
        },
        low: {
          posX: paddingX.right + singleChartWidthSection * 3,
          value: this.container.chart.scale.max - singleChartScale * 3,
        },
      },
    };
    //
    this.ctx!.lineWidth = 1;
    this.ctx!.strokeStyle = 'rgba(158,166,190,0.41)';
    this.ctx!.setLineDash([5, 5]);

    for (const side of ['left', 'right']) {
      for (const range of ['high', 'neutral', 'low']) {
        // bar line
        this.ctx!.beginPath();
        this.ctx!.moveTo(ranges[side][range].posX, this.container.chart.paddingTop);
        this.ctx!.lineTo(ranges[side][range].posX, this.container.chart.height + this.container.chart.paddingTop);
        this.ctx!.stroke();

        this.ctx!.font = '300 12px Open Sans';
        this.ctx!.textAlign = 'center';
        this.ctx!.fillStyle = '#717788';
        this.ctx!.fillText(`${parseFloat(ranges[side][range].value.toFixed(1))} °`, ranges[side][range].posX, paddingY);
      }
    }
    this.ctx!.setLineDash([]);
  }

  // Walk phase values
  runPhases() {
    // Globals
    const singleChartWidth = this.container.chart.width / 2;
    const paddingTextY = 3;
    const startX: any = {
      line: {
        left: this.container.sideLegend.left.endX - 10,
        right: this.container.middle,
      },
      background: {
        left: this.container.sideLegend.left.endX,
        right: this.container.middle,
      },
      legend: {
        left: this.container.sideLegend.left.endX - 20,
        right: this.container.sideLegend.right.startX + 20,
      },
      value: {
        left: this.container.sideLegend.left.endX - 45,
        right: this.container.sideLegend.right.startX + 45,
      },
    };
    const endX: any = {
      line: {
        left: this.container.middle,
        right: this.container.sideLegend.right.startX + 10,
      },
      background: {
        left: singleChartWidth,
        right: singleChartWidth,
      },
    };

    for (const side of ['left', 'right']) {
      this.ctx!.textAlign = side === 'left' ? 'right' : 'left';

      this.footStepsAngles(side, startX, endX, paddingTextY);
      this.footStepsPhases(side, startX, endX, paddingTextY);
    }
  }
  // Propulsion - FlatFoot - Strike
  footStepsAngles(side: string, startX: any, endX: any, paddingTextY: number) {
    // --- BAR LINE --- \\
    this.ctx!.lineWidth = 1;
    this.ctx!.strokeStyle = 'rgba(168,166,169,0.4)';
    this.ctx!.font = '300 12px Open Sans';
    this.ctx!.fillStyle = '#717788';

    for (const angle of ['propulsion', 'flatFoot', 'strike']) {
      this.ctx!.setLineDash([]);
      this.ctx!.beginPath();
      this.ctx!.moveTo(startX.line[side], this.posY[side][angle]);
      this.ctx!.lineTo(endX.line[side], this.posY[side][angle]);
      this.ctx!.stroke();
      // ---
      this.ctx!.fillText(
        i18n.t(`commons.standards.${angle.toLowerCase()}`) as string,
        startX.legend[side],
        this.posY[side][angle] + paddingTextY,
      );
    }
  }
  // PropulsionPhase - StrikePhase
  footStepsPhases(side: string, startX: any, endX: any, paddingTextY: number) {
    const that: any = this;
    const startY = this.container.chart.paddingTop;
    const endY = this.container.chart.height + this.container.chart.paddingTop;
    const valueRect: any = {
      width: 50,
      height: 23,
      startX: startX.value[side] - 25,
      paddingTopY: -15.5,
    };
    const phases: any = {
      propulsionPhase: {
        backgroundcolor: 'rgba(216,220,225,0.35)',
        color: '#5DD0AC',
        firstPosY: startY,
        lastPosY: this.posY[side].flatFoot,
        img: new Image(),
        imgSrc: require('@/assets/images/propulsion.svg'),
      },
      brakingPhase: {
        backgroundcolor: 'rgba(238,242,247,0.35)',
        color: '#5DD0AC',
        firstPosY: this.posY[side].flatFoot,
        lastPosY: endY,
        img: new Image(),
        imgSrc: require('@/assets/images/flat-foot.svg'),
      },
    };
    const chevronsPos: any = {
      left: valueRect.startX - 10,
      right: valueRect.startX + valueRect.width,
      up: -4,
      down: 4,
      posY: {
        up: -2,
        down: -5,
      },
    };

    // --- PHASES --- \\
    this.ctx!.font = '600 12px Open Sans';
    this.ctx!.textAlign = 'center';

    for (const type of ['propulsionPhase', 'brakingPhase']) {
      const width = this.values[type][side].ranges.chevron ? valueRect.width + 18 : valueRect.width;
      const padding = this.values[type][side].ranges.chevron && side === 'left' ? 18 : 0;

      this.ctx!.fillStyle = phases[type].backgroundcolor;

      // Background
      this.ctx!.beginPath();
      this.ctx!.rect(
        startX.background[side],
        phases[type].firstPosY,
        endX.background[side],
        phases[type].lastPosY - phases[type].firstPosY,
      );
      this.ctx!.fill();
      this.ctx!.fillStyle = this.values[type][side].ranges.background;

      // Value
      this.ctx!.beginPath();
      this.ctx!.rect(
        valueRect.startX - padding,
        this.getBlockMiddle(phases[type].firstPosY, phases[type].lastPosY, paddingTextY) + valueRect.paddingTopY,
        width,
        valueRect.height,
      );
      this.ctx!.fill();
      this.ctx!.fillStyle = this.values[type][side].ranges.text;
      this.ctx!.fillText(
        `${this.values[type][side][this.mode].toFixed(0)}${this.unit}`,
        startX.value[side],
        this.getBlockMiddle(phases[type].firstPosY, phases[type].lastPosY, paddingTextY),
      );

      // Chevron
      if (this.values[type][side].ranges.chevron) {
        this.ctx!.lineWidth = 2;
        this.ctx!.strokeStyle = this.values[type][side].ranges.text;
        this.ctx!.beginPath();
        this.ctx!.moveTo(
          chevronsPos[side],
          this.getBlockMiddle(phases[type].firstPosY, phases[type].lastPosY, paddingTextY) +
            chevronsPos.posY[this.values[type][side].ranges.chevron],
        );
        this.ctx!.lineTo(
          chevronsPos[side] + 4,
          this.getBlockMiddle(phases[type].firstPosY, phases[type].lastPosY, paddingTextY) +
            chevronsPos.posY[this.values[type][side].ranges.chevron] +
            chevronsPos[this.values[type][side].ranges.chevron],
        );
        this.ctx!.lineTo(
          chevronsPos[side] + 8,
          this.getBlockMiddle(phases[type].firstPosY, phases[type].lastPosY, paddingTextY) +
            chevronsPos.posY[this.values[type][side].ranges.chevron],
        );
        this.ctx!.stroke();
        this.ctx!.lineWidth = 2;
        this.ctx!.strokeStyle = '#9ea6be';
      }
    }
  }

  // Angle values
  angleLinesAndPoints() {
    // Globals
    const singleChartWidth = this.container.chart.width / 2;
    const paddingX: any = {
      left: this.container.sideLegend.left.endX,
      right: this.container.sideLegend.left.endX + singleChartWidth,
    };
    const reverse: any = {
      left: false,
      right: true,
    };
    this.ctx!.strokeStyle = '#72747c';
    this.ctx!.fillStyle = '#72747c';

    for (const side of ['left', 'right']) {
      // Lines
      this.ctx!.beginPath();
      this.ctx!.moveTo(
        paddingX[side] + this.getPercentPos(this.values.propulsion[side], reverse[side]) * singleChartWidth,
        this.posY[side].propulsion,
      );
      this.ctx!.lineTo(
        paddingX[side] + this.getPercentPos(this.values.flatFoot[side], reverse[side]) * singleChartWidth,
        this.posY[side].flatFoot,
      );
      this.ctx!.lineTo(
        paddingX[side] + this.getPercentPos(this.values.strike[side], reverse[side]) * singleChartWidth,
        this.posY[side].strike,
      );
      this.ctx!.stroke();

      // Points

      for (const angle of ['propulsion', 'flatFoot', 'strike']) {
        this.tooltips.pos[angle][side].x =
          paddingX[side] + this.getPercentPos(this.values[angle][side], reverse[side]) * singleChartWidth;
        this.tooltips.pos[angle][side].y = this.posY[side][angle];
        this.ctx!.beginPath();
        this.ctx!.arc(this.tooltips.pos[angle][side].x, this.tooltips.pos[angle][side].y, 4, 0, 2 * Math.PI);
        this.ctx!.fill();
      }
    }
  }
  // Angle ranges
  angleStdLines() {
    // Globals
    const singleChartWidth = this.container.chart.width / 2;
    const paddingX: any = {
      left: this.container.sideLegend.left.endX,
      right: this.container.sideLegend.left.endX + singleChartWidth,
    };
    const reverse: any = {
      left: false,
      right: true,
    };
    const values: any = {
      propulsion: {},
      flatFoot: {},
      strike: {},
    };

    // Init values
    for (const angle of ['propulsion', 'flatFoot', 'strike']) {
      for (const side of ['left', 'right']) {
        values[angle][side] = {
          min: this.values[angle][side] - this.values[angle][side + 'Std'],
          max: this.values[angle][side] + this.values[angle][side + 'Std'],
          posY: this.posY[side][angle],
        };
      }
    }

    this.ctx!.lineWidth = 2;
    this.ctx!.strokeStyle = 'rgba(140,164,155,0.8)';

    for (const side of ['left', 'right']) {
      for (const level of ['min', 'max']) {
        this.ctx!.beginPath();
        this.ctx!.moveTo(
          paddingX[side] + this.getPercentPos(values.propulsion[side][level], reverse[side]) * singleChartWidth,
          values.propulsion[side].posY,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(values.flatFoot[side][level], reverse[side]) * singleChartWidth,
          values.flatFoot[side].posY,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(values.strike[side][level], reverse[side]) * singleChartWidth,
          values.strike[side].posY,
        );
        this.ctx!.stroke();
      }
    }
  }
  // Angle corridor
  angleCorridor() {
    // Globals
    const singleChartWidth = this.container.chart.width / 2;
    const paddingX: any = {
      left: this.container.sideLegend.left.endX,
      right: this.container.sideLegend.left.endX + singleChartWidth,
    };
    const reverse: any = {
      left: false,
      right: true,
    };
    this.ctx!.fillStyle = 'rgba(93,208,172,0.25)';

    if (this.mode === 'abs') {
      for (const side of ['left', 'right']) {
        this.ctx!.beginPath();
        this.ctx!.moveTo(
          paddingX[side] + this.getPercentPos(this.corridor.propulsion.min, reverse[side]) * singleChartWidth,
          this.posY[side].propulsion,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(this.corridor.flatFoot.min, reverse[side]) * singleChartWidth,
          this.posY[side].flatFoot,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(this.corridor.strike.min, reverse[side]) * singleChartWidth,
          this.posY[side].strike,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(this.corridor.strike.max, reverse[side]) * singleChartWidth,
          this.posY[side].strike,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(this.corridor.flatFoot.max, reverse[side]) * singleChartWidth,
          this.posY[side].flatFoot,
        );
        this.ctx!.lineTo(
          paddingX[side] + this.getPercentPos(this.corridor.propulsion.max, reverse[side]) * singleChartWidth,
          this.posY[side].propulsion,
        );
        this.ctx!.fill();
      }
    }
  }

  // Initialized data
  getTooltipZonePostion() {
    const zoneHeight = 30;
    let zones = {};
    for (const side of ['left', 'right']) {
      for (const type of ['propulsion', 'flatFoot', 'strike']) {
        zones = {
          ...zones,
          type: {},
        };
      }
    }
  }
  getYPositionOfWalkPhases() {
    const height = this.container.chart.height;
    for (const side of ['left', 'right']) {
      const propulsionPhaseSize = height * (this.values.propulsionPhase[side].rel / 100);

      const propulsion = this.container.chart.paddingTop;
      const flatFoot = this.container.chart.paddingTop + propulsionPhaseSize;
      const strike = this.container.chart.height + this.container.chart.paddingTop;

      this.posY[side] = {
        propulsion: propulsion,
        flatFoot: this.getMinYPhasePosition('top', propulsion, flatFoot, strike),
        strike: strike,
      };
    }
  }
  getMinYPhasePosition(type: string, firstValue: number, secondValue: number, thirdValue: number) {
    // If walking phases are too short
    const diff = secondValue - firstValue;
    const minSpace = 50;

    if (type === 'top') {
      // If the space between toesOff and heelOff is to short
      if (diff < minSpace) {
        return firstValue + minSpace;
      }
      // If the space between heelOff and toesStrike is to short
      else if (thirdValue - secondValue < minSpace && thirdValue - secondValue > 25) {
        return secondValue - 10;
      } else if (thirdValue - secondValue <= 25) {
        return secondValue - 20;
      }
      // If the space is good
      else {
        return secondValue;
      }
    } else {
      // If the space between toesStrike and heelStrike is to short
      if (diff < minSpace) {
        return secondValue - minSpace;
      }
      // If the space between heelOff and toesStrike is to short
      else if (thirdValue - firstValue < minSpace && thirdValue - firstValue > 25) {
        return firstValue + 10;
      } else if (thirdValue - firstValue <= 25) {
        return firstValue + 20;
      }
      // If the space is good
      else {
        return firstValue;
      }
    }
  }
  getChartScale() {
    // Get extrem values
    let greaterValue = Math.round(
      Math.max(
        this.values.strike.left + this.values.strike.leftStd,
        this.values.strike.right + this.values.strike.rightStd,
      ),
    );
    let lowestValue = Math.round(
      Math.min(
        this.values.propulsion.left - this.values.strike.leftStd,
        this.values.propulsion.right - this.values.strike.rightStd,
      ),
    );
    const rangeGap = 10;

    let minScale = this.container.chart.scale.min;
    let maxScale = this.container.chart.scale.max;

    // Verif in 10 to 10 if scale is less than value
    for (const level of this.container.chart.scale.zones) {
      if (lowestValue <= this.container.chart.scale.min - level) {
        minScale = this.container.chart.scale.min - level - rangeGap;
        maxScale = this.container.chart.scale.max + level + rangeGap;
      }
      if (greaterValue >= this.container.chart.scale.max + level) {
        maxScale = this.container.chart.scale.max + level + rangeGap;
        minScale = this.container.chart.scale.min - level - rangeGap;
      }
    }

    this.container.chart.scale = {
      min: minScale,
      max: maxScale,
    };
  }
  ifRelativeMode(values: any) {
    // If relative -> rebase all values on toesStrike at 0
    if (this.mode === 'rel') {
      const gap: any = {
        left: values.flatFoot.left * -1,
        right: values.flatFoot.right * -1,
      };

      for (const side of ['left', 'right']) {
        for (const type of ['propulsion', 'flatFoot', 'strike']) {
          this.values[type][side] = values[type][side] + gap[side];
          this.values[type][side + 'Std'] = values[type][side + 'Std'];
        }
        for (const type of ['propulsionPhase', 'brakingPhase']) {
          this.values[type][side] = values[type][side];
        }
      }
    } else {
      this.values = values;
    }
  }

  // Helped functions
  getPercentPos(val: number, reverse: boolean) {
    let result =
      ((val - this.container.chart.scale.min) * 100) /
      (this.container.chart.scale.max - this.container.chart.scale.min);
    result = this.roundNumber(result);

    result = reverse ? 100 - result : result;
    return result / 100;
  }
  getBlockMiddle(firstValue: number, lastValue: number, padding: number) {
    return padding + firstValue + (lastValue - firstValue) / 2;
  }
  roundNumber(val: number) {
    return parseFloat(val.toFixed(1));
  }

  clean() {
    // Store the current transformation matrix
    this.ctx!.save();

    // Use the identity matrix while clearing the canvas
    this.ctx!.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx!.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Restore the transform
    this.ctx!.restore();
    this.ctx!.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
